import { FC, useEffect, useState } from "react";
import { Grid, List, ListItem, ListItemText } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { api } from "@/helper/api";
import styles from "@/components/subjectOverview/subjectOverviewUserInformationCard.module.css";

interface Props {
  detailedSubject: any;
  handleError: (error: any) => void;
}

const SubjectOverviewUserInformationCard: FC<Props> = ({
  detailedSubject,
  handleError,
}) => {
  const { t } = useTranslation();
  const [person, setPerson] = useState<any>(null);

  useEffect(() => {
    if (!detailedSubject?.id || detailedSubject?.is_company) {
      setPerson(null);
      return;
    }
    api.genericApiRequest({
      method: "get",
      entity: "user",
      parametersToRender: {
        subject_id: detailedSubject.id,
        depth: "0",
        include_relations: "true",
      },
      successHandler: (res: any) => {
        setPerson(res.data.results?.[0]?.person ?? null);
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
    //eslint-disable-next-line
  }, [detailedSubject?.id, detailedSubject?.is_company]);

  const customListItem = (props: any) => {
    return (
      <ListItem {...props} sx={{ padding: "0" }}>
        {props.children}
      </ListItem>
    );
  };

  const renderSubjectName = () => {
    let result = detailedSubject?.name || "-";

    if (person?.sex) {
      let tempSex;
      switch (person.sex) {
        case "male":
          tempSex = t("male").toLowerCase();
          break;
        case "female":
          tempSex = t("female").toLowerCase();
          break;
        case "diverse":
          tempSex = t("diverse").toLowerCase();
          break;
      }
      if (tempSex) {
        result += `, ${tempSex}`;
      }
    }
    if (person?.birth_date) {
      const birthDate = dayjs(person.birth_date);
      const age = dayjs().diff(birthDate, "year") + " " + t("year_age");
      result += `, ${birthDate.format("DD.MM.YYYY")} (${age})`;
    }

    return <>{result}</>;
  };

  const renderEmailItem = () => {
    if (!detailedSubject?.email) {
      return "-";
    }
    return (
      <a className={styles.link} href={`mailto:${detailedSubject.email}`}>
        {detailedSubject.email}
      </a>
    );
  };

  const renderCompanyItem = () => {
    const companyName = detailedSubject?.company?.name;
    const companySubjectId = detailedSubject?.company?.subject_id;

    if (!companyName) {
      return "-";
    }
    if (!companySubjectId) {
      return <>{companyName}</>;
    }
    return (
      <Link
        className={styles.link}
        to={`/subjectOverview/${companySubjectId}`}
        target="_blank"
      >
        {companyName}
      </Link>
    );
  };

  return (
    <Grid item container xs={12} component={List} className={styles.container}>
      <Grid xs={12} md={4} item component={customListItem}>
        <ListItemText
          primary={t("customer")}
          secondary={renderSubjectName()}
          primaryTypographyProps={{ className: styles.primaryText }}
          secondaryTypographyProps={{ className: styles.secondaryText }}
        />
      </Grid>
      <Grid xs={12} md={4} item component={customListItem}>
        <ListItemText
          primary={t("email")}
          secondary={renderEmailItem()}
          primaryTypographyProps={{ className: styles.primaryText }}
          secondaryTypographyProps={{ className: styles.secondaryText }}
        />
      </Grid>
      <Grid xs={12} md={4} item component={customListItem}>
        <ListItemText
          primary={t("company")}
          secondary={renderCompanyItem()}
          primaryTypographyProps={{ className: styles.primaryText }}
          secondaryTypographyProps={{ className: styles.secondaryText }}
        />
      </Grid>
    </Grid>
  );
};

export default SubjectOverviewUserInformationCard;
